import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

/*
 * "Inviter til dugnad" — admin drawer on a dugnad convention (type 'dugnad').
 * Two actions: a club-wide push (POST /api/conventions/:id/invite-members,
 * same topic as a new convention) and an optional SMS to club members through
 * the generic sendSms drawer. Recipients come from
 * GET /api/conventions/:id/club-members; members already in `participants`
 * (they showed up) are unticked by default so a reminder reaches only the
 * missing ones.
 */

function formatDugnadDate(startDate) {
    const d = new Date(startDate);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    return d.toLocaleDateString('nb-NO', { timeZone: 'Europe/Oslo' });
}

// The sendSms drawer posts one request per recipient, sequentially, and the
// API flood limiter allows 240 requests/minute per token. A whole-club batch
// can run into that, so warn above this many selected recipients.
const SMS_BATCH_WARN_LIMIT = 150;

// Exported for unit testing.
function needsBatchWarning(count) {
    return Number(count) > SMS_BATCH_WARN_LIMIT;
}

// Pure mapping from API recipients to the sendSms drawer's users[] shape.
// Exported for unit testing.
function buildDugnadSmsUsers(recipients = [], excluded = {}, convention = {}) {
    const dato = formatDugnadDate(convention.startDate);
    return (recipients || [])
        .filter(r => r && r.cellphone && !(excluded || {})[r.id])
        .map(r => ({
            firstname: r.firstname || '',
            lastname: r.lastname || '',
            cellphone: r.cellphone,
            mergeFields: {
                fornavn: r.firstname || '',
                etternavn: r.lastname || '',
                stevne: convention.title || '',
                dato,
            },
        }));
}

@observer
class ConventionViewDugnadInviteAdmin extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            data: null,
            // Selection tracked as EXCLUSIONS (user id -> true), like the
            // training SMS drawer. Members who already showed up start excluded.
            excluded: {},
            pushSending: false,
            pushSent: false,
            pushError: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    load = async () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id) {
            this.setState({ loading: false, error: 'Fant ikke dugnaden' });
            return;
        }
        this.setState({ loading: true, error: null });
        try {
            const response = await util.fetchApi(`/api/conventions/${convention.id}/club-members`, { method: 'GET' }, {});
            if (response?.status === 200) {
                const excluded = {};
                (response.data?.recipients || []).forEach((r) => {
                    if (r.signedUp) {
                        excluded[r.id] = true;
                    }
                });
                this.setState({ loading: false, data: response.data, excluded });
            } else {
                this.setState({ loading: false, error: response?.message || 'Klarte ikke å hente medlemslisten' });
            }
        } catch (err) {
            this.setState({ loading: false, error: err.message || 'Klarte ikke å hente medlemslisten' });
        }
    }

    sendPush = async () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id || this.state.pushSending) {
            return;
        }
        this.setState({ pushSending: true, pushSent: false, pushError: null });
        try {
            const response = await util.fetchApi(`/api/conventions/${convention.id}/invite-members`, { method: 'POST' }, {});
            if (response?.status === 200) {
                this.setState({ pushSending: false, pushSent: true });
            } else {
                this.setState({ pushSending: false, pushError: response?.message || 'Kunne ikke sende push' });
            }
        } catch (err) {
            this.setState({ pushSending: false, pushError: err.message || 'Kunne ikke sende push' });
        }
    }

    toggleSelectRecipient = (e) => {
        const id = parseInt(e.target.dataset.id, 10);
        this.setState((prev) => {
            const excluded = { ...prev.excluded };
            if (excluded[id]) {
                delete excluded[id];
            } else {
                excluded[id] = true;
            }
            return { excluded };
        });
    }

    toggleSelectAll = () => {
        const { excluded, data } = this.state;
        const recipients = data?.recipients || [];
        const allSelected = recipients.length > 0 && recipients.every(r => !excluded[r.id]);
        if (allSelected) {
            const newExcluded = {};
            recipients.forEach((r) => { newExcluded[r.id] = true; });
            this.setState({ excluded: newExcluded });
        } else {
            this.setState({ excluded: {} });
        }
    }

    sendSms = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { excluded, data } = this.state;
        const { drawerHeightMediumLarge } = appState;

        const users = buildDugnadSmsUsers(data?.recipients || [], excluded, convention);
        if (users.length === 0) {
            return;
        }

        appState.openDrawer('sendSms', {
            height: drawerHeightMediumLarge,
            users,
            personalized: true,
            message: `Hei {{fornavn}}! Vi har dugnad {{dato}} ({{stevne}}). Håper vi ser deg! Hilsen ${convention.organizer || 'styret'}`,
            from: convention.title || 'Dugnad',
            // Provider-valid SMS sender (a-zA-Z0-9, max 11 chars); the title is
            // only a seed and is sanitized by the sendSms drawer.
            editableSender: true,
        }, drawerLevel + 1);
    }

    render() {
        const {
            loading, error, data, excluded, pushSending, pushSent, pushError,
        } = this.state;
        const recipients = data?.recipients || [];
        const noPhone = data?.noPhone || [];
        const selectedCount = recipients.filter(r => !excluded[r.id]).length;
        const presentCount = recipients.filter(r => r.signedUp).length + noPhone.filter(r => r.signedUp).length;
        const allSelected = recipients.length > 0 && selectedCount === recipients.length;
        const someSelected = selectedCount > 0 && !allSelected;

        return (<>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3 text-danger'>
                <Text id='convention.dugnad-invite-title'>Invite to dugnad</Text>
            </div>
            <div class='d-flex flex-column mt-0 mx-3 px-3 py-3 rounded-lg bg-body-secondary overflow-auto border border-danger shadow'>

                <div class='d-flex flex-column align-items-center mb-3'>
                    <button
                        type='button'
                        class='btn btn-success rounded-pill'
                        onClick={this.sendPush}
                        disabled={pushSending}
                    >
                        {pushSending ? <i class='fa-duotone fa-spinner-third fa-spin me-2' /> : <i class='fa-duotone fa-bell me-2' />}
                        <Text id='convention.dugnad-invite-push'>Send push to the club</Text>
                    </button>
                    {pushSent && <div class='alert alert-success rounded-lg mt-2 mb-0 py-2 w-100 text-center'>
                        <i class='fa-duotone fa-check me-1' />
                        <Text id='convention.dugnad-invite-push-sent'>Push sent to everyone following the club.</Text>
                    </div>}
                    {pushError && <div class='alert alert-danger rounded-lg mt-2 mb-0 py-2 w-100 text-center'>
                        <Text id='convention.dugnad-invite-push-failed'>Could not send push.</Text> {pushError}
                    </div>}
                </div>

                <div class='font-weight-lighter text-body-secondary mb-2'>
                    <small>
                        <Text id='convention.dugnad-invite-sms-help'>Choose who gets an SMS. Members who already showed up are unticked.</Text>
                    </small>
                </div>

                {loading && <>
                    <div class='text-center py-4'>
                        <i class='fa-duotone fa-spinner fa-spin me-2' />
                        <Text id='convention.dugnad-invite-loading'>Loading members...</Text>
                    </div>
                </>}

                {!loading && error && <>
                    <div class='alert alert-danger'>{error}</div>
                </>}

                {!loading && !error && data && <>
                    <div class='d-flex font-weight-lighter border-bottom mb-2 mt-2'>
                        <Text id='convention.dugnad-invite-counts' fields={{ members: recipients.length, present: presentCount }}>
                            {recipients.length} members, {presentCount} showed up
                        </Text>
                    </div>

                    {recipients.length > 0 ? <>
                        <div class='table-responsive'>
                            <table class='table table-sm table-striped table-hover'>
                                <thead>
                                    <tr>
                                        <th scope='col'>
                                            <input
                                                type='checkbox'
                                                class='form-check-input'
                                                checked={allSelected}
                                                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                                onChange={this.toggleSelectAll}
                                                title='Velg alle / ingen'
                                            />
                                        </th>
                                        <th scope='col'><Text id='convention.dugnad-invite-col-name'>Name</Text></th>
                                        <th scope='col'><Text id='convention.dugnad-invite-col-phone'>Phone</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recipients.map(r => (
                                        <tr key={r.id}>
                                            <td>
                                                <input
                                                    type='checkbox'
                                                    class='form-check-input'
                                                    checked={!excluded[r.id]}
                                                    data-id={r.id}
                                                    onChange={this.toggleSelectRecipient}
                                                />
                                            </td>
                                            <td>
                                                <nobr>
                                                    {r.firstname} {r.lastname}
                                                    {r.signedUp && <span class='badge text-bg-success ms-2'>
                                                        <i class='fa-solid fa-check me-1' />
                                                        <Text id='convention.dugnad-invite-present'>Showed up</Text>
                                                    </span>}
                                                </nobr>
                                            </td>
                                            <td><nobr>{r.cellphone}</nobr></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {needsBatchWarning(selectedCount) && (
                            <div class='alert alert-warning rounded-lg mt-2 py-2'>
                                <i class='fa-duotone fa-triangle-exclamation me-1' />
                                <Text id='convention.dugnad-invite-batch-warning' fields={{ count: selectedCount, limit: SMS_BATCH_WARN_LIMIT }}>
                                    {selectedCount} recipients selected. SMS are sent one by one; send at most {SMS_BATCH_WARN_LIMIT} per batch and untick the rest for a second round.
                                </Text>
                            </div>
                        )}
                        <div class='d-flex flex-wrap justify-content-center mt-2'>
                            <button
                                type='button'
                                class='btn btn-outline-success rounded-pill m-1'
                                onClick={this.sendSms}
                                disabled={selectedCount === 0}
                            >
                                <i class='fa-duotone fa-message-sms me-2' />
                                <Text id='convention.dugnad-invite-send-sms' fields={{ count: selectedCount }}>Send SMS to selected ({selectedCount})</Text>
                            </button>
                        </div>
                    </> : <>
                        <div class='text-body-secondary text-center py-3'>
                            <Text id='convention.dugnad-invite-empty'>No club members with a phone number found.</Text>
                        </div>
                    </>}

                    {noPhone.length > 0 && <>
                        <div class='d-flex font-weight-lighter border-bottom mb-2 mt-3'>
                            <Text id='convention.dugnad-invite-no-phone' fields={{ count: noPhone.length }}>Missing phone number ({noPhone.length}):</Text>
                        </div>
                        <div class='text-body-secondary'>
                            <small>
                                {noPhone.map(p => `${p.firstname || ''} ${p.lastname || ''}`.trim() || `Bruker ${p.id}`).join(', ')}
                            </small>
                        </div>
                    </>}
                </>}
            </div>
        </>);
    }
}

export {
    ConventionViewDugnadInviteAdmin,
    buildDugnadSmsUsers,
    formatDugnadDate,
    needsBatchWarning,
    SMS_BATCH_WARN_LIMIT,
};

export default ConventionViewDugnadInviteAdmin;
